import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { passengerCMSApi, post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useAirportLocDocumentDetails } from "./AirportLocation.hooks";

export const AddEditAirportLocation = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const {
    data: editAirportLoc,
    isLoading: drilldownLoading,
    refetch: refetchCategories,
  } = useAirportLocDocumentDetails(query.get("editId") || "");

  const {
    data: editAirportByIdLoc,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [airportOptions, setAirportOptions] = useState<any[]>([]);
  const [airportChildOptions, setAirportChildOptions] = useState<any[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  const fetchParentDropdown = async () => {
    try {
      const { data } = await passengerCMSApi.get(`/api/airport-category/parent-term`);

      const filteredData = data?.filter((item: any) => item.published === "true") || [];

      setAirportOptions(
        filteredData.map((item: any) => ({
          label: decodeHTML(item.parent_term_name),
          value: String(item.parent_term_id),
        }))
      );
    } catch (error) {
      console.error("Dropdown fetch failed", error);
    }
  };

  const fetchChildDropdown = async (parentId: string) => {
    try {
      const { data } = await passengerCMSApi.get(`/api/airport-category/child-term?parent=${parentId}`);
      setAirportChildOptions(
        data?.map((item: any) => ({
          label: decodeHTML(item.name),
          value: String(item.id),
        })) || []
      );
    } catch (error) {
      console.error("Child dropdown fetch failed", error);
    }
  };

  useEffect(() => {
    fetchParentDropdown();
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("editId"), query.get("id")]);

  useEffect(() => {
    if (selectedParentId) {
      const handler = setTimeout(() => fetchChildDropdown(selectedParentId), 150);
      return () => clearTimeout(handler);
    } else {
      setAirportChildOptions([]);
    }
  }, [selectedParentId]);

  useEffect(() => {
    const parentTerm = editAirportByIdLoc?.payload?.parent_term_name || editAirportLoc?.parent_term_name;
    const parentId =
      typeof parentTerm === "object"
        ? parentTerm?.value
        : editAirportByIdLoc?.payload?.parent_term_id || editAirportLoc?.parent_term_id || parentTerm;
    if (parentId) {
      setSelectedParentId(String(parentId));
    }
  }, [editAirportByIdLoc, editAirportLoc]);

  const handleParentChange = (selected: any) => {
    // if full object (from Select), set its id for fetching child dropdowns
    if (typeof selected === "object" && selected?.value) {
      setSelectedParentId(String(selected.value));
    } else {
      setSelectedParentId(String(selected));
    }
  };

  const initialValues = useMemo(() => {
    return {
      title: decodeHTML(editAirportByIdLoc?.payload?.title || editAirportLoc?.title || ""),
      parent_term_name:
        typeof editAirportByIdLoc?.payload?.parent_term_name === "object"
          ? editAirportByIdLoc?.payload?.parent_term_name
          : editAirportByIdLoc?.payload?.parent_term_id
            ? {
                value: String(editAirportByIdLoc?.payload?.parent_term_id),
                label: editAirportByIdLoc?.payload?.parent_term_name || "",
              }
            : typeof editAirportLoc?.parent_term_name === "object"
              ? editAirportLoc?.parent_term_name
              : editAirportLoc?.parent_term_id
                ? {
                    value: String(editAirportLoc?.parent_term_id),
                    label: editAirportLoc?.parent_term_name || "",
                  }
                : null,
      name: decodeHTML(editAirportByIdLoc?.payload?.name || editAirportLoc?.name || null),

      // key: decodeHTML(editAirportByIdLoc?.payload?.key || editAirportLoc?.key || ""),
      address_english: decodeHTML(
        editAirportByIdLoc?.payload?.address_english || editAirportLoc?.address_english || ""
      ),
      address_malay: decodeHTML(editAirportByIdLoc?.payload?.address_malay || editAirportLoc?.address_malay || ""),
      address_mandarin: decodeHTML(
        editAirportByIdLoc?.payload?.address_mandarin || editAirportLoc?.address_mandarin || ""
      ),
      location_direction: decodeHTML(
        editAirportByIdLoc?.payload?.location_direction || editAirportLoc?.location_direction || ""
      ),
      fax: decodeHTML(editAirportByIdLoc?.payload?.fax || editAirportLoc?.fax || ""),
      latitude: decodeHTML(editAirportByIdLoc?.payload?.latitude || editAirportLoc?.latitude || ""),
      longitude: decodeHTML(editAirportByIdLoc?.payload?.longitude || editAirportLoc?.longitude || ""),
      short_form: decodeHTML(editAirportByIdLoc?.payload?.short_form || editAirportLoc?.short_form || ""),
      tel: decodeHTML(editAirportByIdLoc?.payload?.tel || editAirportLoc?.tel || ""),
      // value: decodeHTML(editAirportByIdLoc?.payload?.value || editAirportLoc?.value || ""),
      // airport_location_redirect: decodeHTML(
      //   editAirportByIdLoc?.payload?.airport_location_redirect || editAirportLoc?.airport_location_redirect || ""
      // ),

      published: editAirportByIdLoc
        ? decodeHTML(editAirportByIdLoc.payload.published)
        : editAirportLoc
          ? decodeHTML(editAirportLoc?.published)
          : false,
    };
  }, [editAirportByIdLoc, editAirportLoc]);

  const formConfig: FormConfig = useMemo(() => {
    return {
      initialValues,
      fields: fields.airportLocations.map((field: any) => {
        if (field.name === "parent_term_name") {
          return {
            ...field,
            options: airportOptions,
            onChange: handleParentChange,
          };
        }
        if (field.name === "name") {
          return {
            ...field,
            options: airportChildOptions,
          };
        }
        return field;
      }),
    };
  }, [airportOptions, airportChildOptions, initialValues]);

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    console.log("data-123", data);

    let imageKeys: any = ["field_common_image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        }
      } catch (error) {}
    }

    if (data.parent_term_name) {
      let selectedOption: any = null;

      // Handle both object and string cases safely
      if (typeof data.parent_term_name === "object" && data.parent_term_name?.value) {
        selectedOption = airportOptions.find((opt) => opt.value === data.parent_term_name.value);
      } else {
        selectedOption = airportOptions.find((opt) => opt.value === String(data.parent_term_name));
      }

      // ✅ Always send as { value, label }
      if (selectedOption) {
        data.parent_term_name = {
          value: String(selectedOption.value),
          label: selectedOption.label,
        };
      } else if (typeof data.parent_term_name === "string") {
        data.parent_term_name = {
          value: data.parent_term_name,
          label: data.parent_term_name,
        };
      }
    }

    if (data.name) {
      const selectedChild = airportChildOptions.find(
        (opt) => opt.value === (data.name?.value ?? data.name) || opt.label === (data.name?.label ?? data.name)
      );
      data.name = selectedChild
        ? { value: String(selectedChild.value), label: selectedChild.label }
        : typeof data.name === "string"
          ? { value: data.id, label: data.name }
          : data.name;
    }

    const payload = {
      action,
      method,
      module: "Maps",
      subModule: "airportLocations",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: {
        ...(action === "UPDATE" && { id: editAirportLoc?.id || editAirportByIdLoc?.payload?.id }),
        ...data,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Airport Locations`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${
              query.get("editId") || query.get("type") === "update"
                ? `${passengerHost}/api/airport-locations-crud-data/${editAirportLoc?.id || editAirportByIdLoc?.payload?.id}`
                : `${passengerHost}/api/create-airport-locations-crud-data`
            }`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Maps"
      />
    </ContentWrapper>
  );
};
